import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Activity, Flame, Award, CircleDot } from "lucide-react";

/**
 * Stats page with scope switch: Saison vs. Alle Jahre, plus H2H.
 */
export default function Stats() {
  const [scope, setScope] = useState("season"); // season | all
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [players, setPlayers] = useState([]);
  const [leaders, setLeaders] = useState(null);
  const [tab, setTab] = useState("leaders");
  const [p1, setP1] = useState(""); const [p2, setP2] = useState("");
  const [h2h, setH2h] = useState(null);

  useEffect(() => {
    (async () => {
      const [ss, ps] = await Promise.all([api.listSeasons(), api.listPlayers()]);
      setSeasons(ss); setPlayers(ps);
      const active = ss.find((s) => s.active) || ss[0];
      if (active) setSeasonId(active.id);
    })();
  }, []);

  useEffect(() => {
    if (scope === "all") {
      api.leaderboardsAll().then(setLeaders);
    } else if (seasonId) {
      api.leaderboardsSeason(seasonId).then(setLeaders);
    }
  }, [scope, seasonId]);

  const loadH2H = async () => {
    if (!p1 || !p2 || p1 === p2) return;
    const data = await api.h2h(p1, p2, scope === "season" ? seasonId : undefined);
    setH2h(data);
  };

  const p2Options = useMemo(() => players.filter((p) => p.id !== p1), [players, p1]);

  return (
    <div className="space-y-6" data-testid="stats-page">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>Statistik</div>
          <h1 className="font-display text-5xl">Numbers Don&apos;t Lie</h1>
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex dc-border">
            <button className={`px-3 py-2 text-xs uppercase tracking-widest ${scope === "season" ? "text-white" : ""}`}
              style={{ background: scope === "season" ? "var(--dc-secondary)" : "transparent" }}
              onClick={() => setScope("season")} data-testid="stats-scope-season">Saison</button>
            <button className={`px-3 py-2 text-xs uppercase tracking-widest ${scope === "all" ? "text-white" : ""}`}
              style={{ background: scope === "all" ? "var(--dc-secondary)" : "transparent" }}
              onClick={() => setScope("all")} data-testid="stats-scope-all">Alle Jahre</button>
          </div>
          {scope === "season" && (
            <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="stats-season-select">
              {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b dc-border">
        <button className={`dc-tab ${tab === "leaders" ? "active" : ""}`} onClick={() => setTab("leaders")} data-testid="stats-tab-leaders">Leaderboards</button>
        <button className={`dc-tab ${tab === "h2h" ? "active" : ""}`} onClick={() => setTab("h2h")} data-testid="stats-tab-h2h">Head to Head</button>
      </div>

      {tab === "leaders" && leaders && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Leaderboard title="Bester Average" icon={Activity} rows={leaders.average} valueKey="average" color="var(--dc-secondary)" testid="lb-avg" />
          <Leaderboard title="Meiste 180er" icon={Flame} rows={leaders.s180} valueKey="s180" color="var(--dc-primary)" testid="lb-180" />
          <Leaderboard title="Highest Finish" icon={Award} rows={leaders.high_finish} valueKey="high_finish" color="var(--dc-chalk)" testid="lb-hf" />
          <Leaderboard title="Ausgebullte Legs" icon={CircleDot} rows={leaders.ausgebullt} valueKey="ausgebullt" color="var(--dc-text)" testid="lb-bull" />
        </div>
      )}

      {tab === "h2h" && (
        <div className="space-y-6">
          <div className="dc-border p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Spieler 1</label>
              <select className="dc-input" value={p1} onChange={(e) => setP1(e.target.value)} data-testid="h2h-p1">
                <option value="">-- wählen --</option>
                {players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Spieler 2</label>
              <select className="dc-input" value={p2} onChange={(e) => setP2(e.target.value)} data-testid="h2h-p2">
                <option value="">-- wählen --</option>
                {p2Options.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <button className="dc-btn" onClick={loadH2H} data-testid="h2h-compare-button">Vergleichen</button>
          </div>

          {h2h && <H2HResult data={h2h} />}
        </div>
      )}
    </div>
  );
}

function Leaderboard({ title, icon: Icon, rows, valueKey, color, testid }) {
  return (
    <div className="dc-border p-6" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} style={{ color }} />
        <h3 className="font-display text-2xl">{title}</h3>
      </div>
      <table className="w-full font-mono-data text-sm">
        <tbody>
          {(rows || []).slice(0, 10).map((r, i) => (
            <tr key={r.player_id} className="row-in border-t dc-border" style={{ animationDelay: `${i * 30}ms` }} data-testid={`${testid}-row-${r.player_id}`}>
              <td className="py-2 w-8 text-center" style={{ color: "var(--dc-muted)" }}>{i + 1}</td>
              <td className="py-2" style={{ fontFamily: "Manrope" }}>{r.name}</td>
              <td className="py-2 text-right font-bold" style={{ color }}>{r[valueKey] || 0}</td>
            </tr>
          ))}
          {(!rows || rows.length === 0) && <tr><td className="py-6 text-center" style={{ color: "var(--dc-muted)" }} colSpan={3}>Keine Daten</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function H2HResult({ data }) {
  const { player1, player2, p1_wins, p2_wins, p1_legs, p2_legs, matches, p1_stats, p2_stats } = data;
  return (
    <div className="space-y-4" data-testid="h2h-result">
      <div className="dc-border p-8">
        <div className="grid grid-cols-3 items-center gap-4">
          <div className="text-center md:text-right"><div className="font-display text-4xl">{player1.name}</div></div>
          <div className="text-center">
            <div className="font-mono-data font-display text-7xl flex items-center justify-center gap-4">
              <span style={{ color: p1_wins > p2_wins ? "var(--dc-secondary)" : "var(--dc-text)" }}>{p1_wins}</span>
              <span style={{ color: "var(--dc-muted)" }}>:</span>
              <span style={{ color: p2_wins > p1_wins ? "var(--dc-secondary)" : "var(--dc-text)" }}>{p2_wins}</span>
            </div>
            <div className="text-xs uppercase tracking-widest mt-2" style={{ color: "var(--dc-muted)" }}>Matches</div>
            <div className="font-mono-data text-sm mt-3">Legs: <span className="text-white">{p1_legs}</span> : <span className="text-white">{p2_legs}</span></div>
          </div>
          <div className="text-center md:text-left"><div className="font-display text-4xl">{player2.name}</div></div>
        </div>
      </div>

      <div className="dc-border p-6">
        <h3 className="font-display text-2xl mb-4">Statistik</h3>
        <table className="w-full font-mono-data text-sm">
          <thead style={{ color: "var(--dc-muted)" }} className="text-xs uppercase">
            <tr>
              <th className="text-right py-2 pr-3">{player1.name}</th>
              <th className="text-center">Kennzahl</th>
              <th className="text-left py-2 pl-3">{player2.name}</th>
            </tr>
          </thead>
          <tbody>
            {[["average","Average"],["s180","180er"],["high_finish","High Finish"],["ausgebullt","Ausgebullt"],["legs_won","Legs gewonnen"]].map(([k,label]) => (
              <tr key={k} className="border-t dc-border">
                <td className="py-2 pr-3 text-right text-white">{p1_stats[k] || 0}</td>
                <td className="text-center text-xs uppercase" style={{ color: "var(--dc-muted)" }}>{label}</td>
                <td className="py-2 pl-3 text-left text-white">{p2_stats[k] || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
